import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Coachmark from './tutorial/Coachmark';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Battle.css';
import './Village.css';
import './ItemMarket.css';
import AuthLayeredCard from './AuthLayeredCard';
import { authButtonSmallStyle, authButtonStyle } from '../constants/authStyles';
import { useItemMarket } from '../context/ItemMarketContext';
import { useCharacter } from '../context/CharacterContext';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { formatDateTime, formatNumber } from '../utils/localeFormat';
import { useRewardedAds, adBoostRemaining } from '../utils/rewardedAds';
import { tl } from '../utils/inlineI18n';
import { isText2MobileApp, purchaseRuhparasiPackageNative, resolveStoreProductId } from '../utils/mobileIapBridge';

const INVENTORY_MAX_SLOTS = 120;
const INVENTORY_DEFAULT_UNLOCKED_SLOTS = 60;

const marketImageSrc = (rawUrl) => {
  if (!rawUrl) return '/image/market.png';
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('/')) {
    return rawUrl;
  }
  return `/${rawUrl}`;
};

const ItemMarket = () => {
  const { t } = useTranslation('gameData');
  const { t: tc } = useTranslation('common');
  const { isOpen, closeMarket } = useItemMarket();
  const { refreshCharacter } = useCharacter();
  const { setError, setStatus } = usePopupMessage();
  const ads = useRewardedAds();
  const [, setAdTick] = useState(0);
  const adExpLeft = adBoostRemaining(ads.status?.adExpBoostUntil);
  const adDropLeft = adBoostRemaining(ads.status?.adDropBoostUntil);
  useEffect(() => {
    if (!adExpLeft && !adDropLeft) return undefined;
    const id = setInterval(() => setAdTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [adExpLeft, adDropLeft]);
  const adPurposeFor = (product) => (product?.type === 'exp_boost' ? 'exp_boost' : product?.type === 'drop_boost' ? 'drop_boost' : '');
  const watchBoostAd = async (purpose, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!purpose) return;
    const res = await ads.watch(purpose);
    if (res?.success) setStatus(res.message || '');
    else if (res?.message) setError(res.message);
  };
  // Ücretsiz Ruh Parası: günde 10 video, her video 1 Ruh (görevlerdeki "Günlük Video" ile aynı hak).
  const adRuhCount = ads.status?.counts?.ruh ?? 0;
  const adRuhLimit = ads.status?.limits?.ruh ?? 10;
  const adRuhDone = adRuhCount >= adRuhLimit;
  const watchRuhAd = async () => {
    if (adRuhDone || ads.watching) return;
    const res = await ads.watch('ruh');
    if (res?.success) {
      setStatus(res.message || tl('+1 Ruh Parası', '+1 Soul Coin'));
      refreshCharacter?.()?.catch?.(() => {});
    } else if (res?.message) setError(res.message);
  };
  const renderAdBoostButton = (product, compact) => {
    const purpose = adPurposeFor(product);
    if (!ads.supported || !purpose) return null;
    const left = purpose === 'exp_boost' ? adExpLeft : adDropLeft;
    const used = ads.status?.counts?.[purpose] ?? 0;
    const limit = ads.status?.limits?.[purpose] ?? 0;
    const disabled = !!ads.watching || !!left || (limit > 0 && used >= limit);
    const label = left
      ? `🎬 ${left}`
      : ads.watching === purpose
        ? '…'
        : compact
          ? tl('🎬 10 dk ücretsiz', '🎬 10 min free')
          : tl('🎬 Video izle (10 dk ücretsiz)', '🎬 Watch video (10 min free)');
    return (
      <span
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
        className={`item-market-ad-btn${disabled ? ' item-market-ad-btn--disabled' : ''}`}
        onClick={(e) => (disabled ? e.stopPropagation() : watchBoostAd(purpose, e))}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) watchBoostAd(purpose, e);
        }}
      >
        {label}
      </span>
    );
  };
  const username = localStorage.getItem('username');

  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [ruhParasi, setRuhParasi] = useState(0);
  const [inventoryUnlockedSlots, setInventoryUnlockedSlots] = useState(INVENTORY_DEFAULT_UNLOCKED_SLOTS);
  const [expBoostUntil, setExpBoostUntil] = useState(0);
  const [dropBoostUntil, setDropBoostUntil] = useState(0);
  const [fishingGuideUntil, setFishingGuideUntil] = useState(0);
  const [minerBookUntil, setMinerBookUntil] = useState(0);
  const [masterMinerBookUntil, setMasterMinerBookUntil] = useState(0);
  const [buyingId, setBuyingId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const formatDurationHours = useCallback((hours) => {
    if (!hours) return '';
    if (hours === 24) return t('itemMarketDurationDay');
    if (hours === 168) return t('itemMarketDurationWeek');
    if (hours === 720) return t('itemMarketDurationMonth');
    return t('itemMarketDurationHours', { hours });
  }, [t]);

  const formatBuffExpiry = useCallback((until) => {
    if (!until || until <= Math.floor(Date.now() / 1000)) return null;
    return formatDateTime(until * 1000, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatTryPrice = useCallback((price) => {
    const num = Number(price);
    if (Number.isNaN(num)) return t('itemMarketPriceTry', { price });
    return t('itemMarketPriceTry', {
      price: formatNumber(num, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    });
  }, [t]);

  const fetchStatus = useCallback(async () => {
    if (!username) return;
    try {
      const res = await axios.get(`/api/item-market/status?username=${encodeURIComponent(username)}`);
      if (res.data?.success) {
        setRuhParasi(res.data.ruhParasi ?? 0);
        setInventoryUnlockedSlots(res.data.inventoryUnlockedSlots ?? INVENTORY_DEFAULT_UNLOCKED_SLOTS);
        setExpBoostUntil(res.data.expBoostUntil ?? 0);
        setDropBoostUntil(res.data.dropBoostUntil ?? 0);
        setFishingGuideUntil(res.data.fishingGuideUntil ?? 0);
        setMinerBookUntil(res.data.minerBookUntil ?? 0);
        setMasterMinerBookUntil(res.data.masterMinerBookUntil ?? 0);
      }
    } catch (err) {
      console.error('Item market status error:', err);
    }
  }, [username]);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await axios.get('/api/item-market/catalog');
      if (res.data?.success) {
        setProducts(res.data.products || []);
        setPackages(res.data.packages || []);
      }
    } catch (err) {
      console.error('Item market catalog error:', err);
      setError(t('itemMarketCatalogFailed'));
    }
  }, [setError, t]);

  useEffect(() => {
    if (!isOpen || !username) return undefined;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchCatalog(), fetchStatus()]);
      if (!cancelled) setLoading(false);
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, username, fetchCatalog, fetchStatus]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedProduct(null);
      setSelectedPackage(null);
    }
  }, [isOpen]);

  const getProductMeta = useCallback((product) => {
    const detail = product.durationHours
      ? t('itemMarketDetailDuration', { duration: formatDurationHours(product.durationHours) })
      : product.grantCount
        ? t('itemMarketDetailCount', { count: product.grantCount })
        : '';
    const isInventoryUnlock = product.type === 'inventory_unlock';
    const inventoryFullyUnlocked = inventoryUnlockedSlots >= INVENTORY_MAX_SLOTS;
    const cannotBuyInventoryUnlock = isInventoryUnlock && inventoryFullyUnlocked;
    const cannotAfford = ruhParasi < (product.priceSoul || 0);
    return { detail, isInventoryUnlock, cannotBuyInventoryUnlock, cannotAfford };
  }, [t, formatDurationHours, inventoryUnlockedSlots, ruhParasi]);

  const groupedProducts = useMemo(() => {
    const groups = {};
    products.forEach((product) => {
      const category = product.category || t('itemMarketCategoryOther');
      if (!groups[category]) groups[category] = [];
      groups[category].push(product);
    });
    return groups;
  }, [products, t]);

  const expBoostExpiry = formatBuffExpiry(expBoostUntil);
  const dropBoostExpiry = formatBuffExpiry(dropBoostUntil);
  const fishingGuideExpiry = formatBuffExpiry(fishingGuideUntil);
  const minerBookExpiry = formatBuffExpiry(minerBookUntil);
  const masterMinerBookExpiry = formatBuffExpiry(masterMinerBookUntil);

  const handleBuyProduct = async (productId) => {
    if (!username || buyingId) return;
    setBuyingId(productId);
    try {
      const res = await axios.post('/api/item-market/buy-product', { username, productId });
      if (res.data?.success) {
        setStatus(res.data.message || t('itemMarketBuySuccess'));
        setSelectedProduct(null);
        if (typeof res.data.ruhParasi === 'number') setRuhParasi(res.data.ruhParasi);
        if (typeof res.data.inventoryUnlockedSlots === 'number') {
          setInventoryUnlockedSlots(res.data.inventoryUnlockedSlots);
        }
        if (typeof res.data.expBoostUntil === 'number') setExpBoostUntil(res.data.expBoostUntil);
        if (typeof res.data.dropBoostUntil === 'number') setDropBoostUntil(res.data.dropBoostUntil);
        if (typeof res.data.fishingGuideUntil === 'number') setFishingGuideUntil(res.data.fishingGuideUntil);
        if (typeof res.data.minerBookUntil === 'number') setMinerBookUntil(res.data.minerBookUntil);
        if (typeof res.data.masterMinerBookUntil === 'number') setMasterMinerBookUntil(res.data.masterMinerBookUntil);
        await refreshCharacter().catch(() => {});
        await fetchStatus();
      } else {
        setError(res.data?.message || t('itemMarketBuyFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('itemMarketBuyFailed'));
    } finally {
      setBuyingId(null);
    }
  };

  const handleBuyPackage = async (pkg) => {
    if (!username || buyingId) return;
    setBuyingId(`pkg-${pkg.id}`);
    try {
      if (isText2MobileApp()) {
        const productId = resolveStoreProductId(pkg);
        if (!productId) {
          setError(t('itemMarketBuyFailed'));
          return;
        }
        const result = await purchaseRuhparasiPackageNative(pkg.id, productId);
        if (result && result.success) {
          setStatus(result.message || t('itemMarketPackageSuccess'));
          setSelectedPackage(null);
          if (typeof result.ruhParasi === 'number') setRuhParasi(result.ruhParasi);
          await fetchStatus();
        } else {
          setError((result && result.message) || t('itemMarketBuyFailed'));
        }
        return;
      }

      const res = await axios.post('/api/item-market/buy-package', { username, packageId: pkg.id });
      if (res.data?.success) {
        setStatus(res.data.message || t('itemMarketPackageSuccess'));
        setSelectedPackage(null);
        if (typeof res.data.ruhParasi === 'number') setRuhParasi(res.data.ruhParasi);
        await fetchStatus();
      } else {
        setError(res.data?.message || t('itemMarketBuyFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('itemMarketBuyFailed'));
    } finally {
      setBuyingId(null);
    }
  };

  const renderProductDetail = (product) => {
    const { detail, isInventoryUnlock, cannotBuyInventoryUnlock, cannotAfford } = getProductMeta(product);
    const isBuying = buyingId === product.id;
    return (
      <div className="item-market-detail">
        <button
          type="button"
          className="item-market-detail__back auth-button auth-button--compact"
          style={authButtonSmallStyle}
          onClick={() => setSelectedProduct(null)}
        >
          {t('itemMarketDetailBack')}
        </button>
        <div className="item-market-detail__hero">
          <img
            src={marketImageSrc(product.imageUrl)}
            alt=""
            className="item-market-detail__image"
            draggable={false}
          />
        </div>
        <h3 className="item-market-detail__title">{product.name}</h3>
        {renderAdBoostButton(product, false)}
        {product.category ? (
          <p className="item-market-detail__category">{product.category}</p>
        ) : null}
        {product.description ? (
          <p className="item-market-detail__desc">{product.description}</p>
        ) : null}
        {detail ? <p className="item-market-detail__meta">{detail}</p> : null}
        {isInventoryUnlock ? (
          <p className="item-market-detail__meta">
            {t('itemMarketOpenSlotDetail', { current: inventoryUnlockedSlots, max: INVENTORY_MAX_SLOTS })}
          </p>
        ) : null}
        {product.boostPercent ? (
          <p className="item-market-detail__meta">
            {t('itemMarketDetailBoost', { percent: product.boostPercent })}
          </p>
        ) : null}
        <p className="item-market-detail__price">
          {t('itemMarketPriceSoul', { price: product.priceSoul })}
        </p>
        <button
          type="button"
          className="auth-button auth-button--compact item-market-detail__buy"
          style={authButtonStyle}
          disabled={isBuying || cannotBuyInventoryUnlock || cannotAfford}
          onClick={() => handleBuyProduct(product.id)}
        >
          {isBuying
            ? '...'
            : cannotBuyInventoryUnlock
              ? t('itemMarketAllSlotsOpen')
              : cannotAfford
                ? t('itemMarketInsufficientSoul')
                : tc('buy')}
        </button>
      </div>
    );
  };

  const renderPackageDetail = (pkg) => {
    const isBuying = buyingId === `pkg-${pkg.id}`;
    return (
      <div className="item-market-detail">
        <button
          type="button"
          className="item-market-detail__back auth-button auth-button--compact"
          style={authButtonSmallStyle}
          onClick={() => setSelectedPackage(null)}
        >
          {t('itemMarketDetailBack')}
        </button>
        <div className="item-market-detail__hero">
          <img src="/image/market/ruh_parasi.png" alt="" className="item-market-detail__image" draggable={false} />
        </div>
        <h3 className="item-market-detail__title">
          {t('itemMarketPackageAmount', { amount: formatNumber(pkg.amount) })}
        </h3>
        <p className="item-market-detail__desc">{t('itemMarketPackageDesc')}</p>
        <p className="item-market-detail__price">{formatTryPrice(pkg.priceTry)}</p>
        <button
          type="button"
          className="auth-button auth-button--compact item-market-detail__buy"
          style={authButtonStyle}
          disabled={isBuying}
          onClick={() => handleBuyPackage(pkg)}
        >
          {isBuying ? '...' : tc('buy')}
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay item-market-overlay" onClick={closeMarket} role="presentation">
      <Coachmark
        tourId="itemMarket"
        steps={[
          { selector: '.item-market-tabs', textKey: 'marketTabs' },
          { selector: '.item-market-category', textKey: 'marketProducts' },
          { selector: '.item-market-ad-btn', textKey: 'marketAdVideo' },
      ]}
      />
      <AuthLayeredCard
        className="item-market-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="item-market-header">
          <div className="item-market-header__info">
            <h2 className="item-market-title">{t('itemMarketTitle')}</h2>
            <div className="item-market-balance">
              {tc('soulMoney')}: <span className="item-market-balance__value">{ruhParasi}</span>
            </div>
            <div className="item-market-balance">
              {t('itemMarketOpenSlotsLabel')}:{' '}
              <span className="item-market-balance__value">
                {inventoryUnlockedSlots} / {INVENTORY_MAX_SLOTS}
              </span>
            </div>
            {(expBoostExpiry || dropBoostExpiry || adExpLeft || adDropLeft || fishingGuideExpiry || minerBookExpiry || masterMinerBookExpiry) && (
              <div className="item-market-buffs">
                {expBoostExpiry && (
                  <span className="item-market-buff">{t('itemMarketBuffExp')}: {expBoostExpiry}</span>
                )}
                {dropBoostExpiry && (
                  <span className="item-market-buff">{t('itemMarketBuffDrop')}: {dropBoostExpiry}</span>
                )}
                {adExpLeft && (
                  <span className="item-market-buff">🎬 {t('itemMarketBuffExp')}: {adExpLeft}</span>
                )}
                {adDropLeft && (
                  <span className="item-market-buff">🎬 {t('itemMarketBuffDrop')}: {adDropLeft}</span>
                )}
                {fishingGuideExpiry && (
                  <span className="item-market-buff">{t('itemMarketBuffFishingGuide')}: {fishingGuideExpiry}</span>
                )}
                {minerBookExpiry && (
                  <span className="item-market-buff">{t('itemMarketBuffMinerBook')}: {minerBookExpiry}</span>
                )}
                {masterMinerBookExpiry && (
                  <span className="item-market-buff">{t('itemMarketBuffMasterMinerBook')}: {masterMinerBookExpiry}</span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="item-market-close"
            onClick={closeMarket}
            aria-label={tc('close')}
          >
            ✕
          </button>
        </div>

        <div className="battle-nav-row item-market-tabs">
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'products' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => {
              setActiveTab('products');
              setSelectedPackage(null);
            }}
          >
            {t('itemMarketTabsProducts')}
          </button>
          <button
            type="button"
            className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'packages' ? ' battle-subtab--active' : ''}`}
            style={authButtonSmallStyle}
            onClick={() => {
              setActiveTab('packages');
              setSelectedProduct(null);
            }}
          >
            {t('itemMarketTabsBuySoul')}
          </button>
        </div>

        <div className="item-market-body">
          {loading ? (
            <div className="item-market-loading">{tc('loading')}</div>
          ) : activeTab === 'products' ? (
            selectedProduct ? (
              renderProductDetail(selectedProduct)
            ) : (
              <div className="item-market-products">
                {Object.keys(groupedProducts).length === 0 ? (
                  <p className="item-market-empty">{t('itemMarketEmptyProducts')}</p>
                ) : (
                  Object.entries(groupedProducts).map(([category, items]) => (
                    <section key={category} className="item-market-category">
                      <h3 className="item-market-category__title">
                        {category}
                        {renderAdBoostButton(items[0], true)}
                      </h3>
                      <div className="item-market-grid item-market-grid--tiles">
                        {items.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            className="item-market-tile"
                            onClick={() => setSelectedProduct(product)}
                          >
                            <span className="item-market-tile__frame">
                              <img
                                src={marketImageSrc(product.imageUrl)}
                                alt=""
                                className="item-market-tile__img"
                                draggable={false}
                              />
                            </span>
                            <span className="item-market-tile__name">{product.name}</span>
                            <span className="item-market-tile__price">
                              {t('itemMarketPriceSoul', { price: product.priceSoul })}
                            </span>
                            <span className="item-market-tile__hint">{t('itemMarketTapHint')}</span>
                          </button>
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>
            )
          ) : selectedPackage ? (
            renderPackageDetail(selectedPackage)
          ) : (
            <div className="item-market-packages">
              {packages.length === 0 ? (
                <p className="item-market-empty">{t('itemMarketEmptyPackages')}</p>
              ) : (
                <div className="item-market-grid item-market-grid--tiles">
                  {ads.supported ? (
                    <button
                      type="button"
                      className={`item-market-tile item-market-tile--package item-market-tile--free-video${adRuhDone ? ' is-done' : ''}`}
                      onClick={watchRuhAd}
                      disabled={adRuhDone || !!ads.watching}
                    >
                      <span className="item-market-tile__frame">
                        <img src="/image/market/ruh_parasi.png" alt="" className="item-market-tile__img" draggable={false} />
                        <span className="item-market-tile__video-mark" aria-hidden="true">🎬</span>
                      </span>
                      <span className="item-market-tile__name">
                        {tl(`Ücretsiz ${adRuhLimit} Ruh Parası`, `Free ${adRuhLimit} Soul Coins`)}
                      </span>
                      <span className="item-market-tile__price">
                        {adRuhDone ? tl('Bugünlük bitti', 'Done for today') : `${adRuhCount}/${adRuhLimit}`}
                      </span>
                      <span className="item-market-tile__hint">
                        {ads.watching === 'ruh' ? '…' : adRuhDone ? tl('Yarın yine gel', 'Come back tomorrow') : tl('Video izle: +1 Ruh', 'Watch video: +1 Soul')}
                      </span>
                    </button>
                  ) : null}
                  {packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      className="item-market-tile item-market-tile--package"
                      onClick={() => setSelectedPackage(pkg)}
                    >
                      <span className="item-market-tile__frame">
                        <img src="/image/market/ruh_parasi.png" alt="" className="item-market-tile__img" draggable={false} />
                      </span>
                      <span className="item-market-tile__name">
                        {t('itemMarketPackageAmount', { amount: formatNumber(pkg.amount) })}
                      </span>
                      <span className="item-market-tile__price item-market-tile__price--try">
                        {formatTryPrice(pkg.priceTry)}
                      </span>
                      <span className="item-market-tile__hint">{t('itemMarketTapHint')}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </AuthLayeredCard>
    </div>
  );
};

export default ItemMarket;
